/* Opus Gift Guide 2026 — small progressive-enhancement helpers. Everything works without JS except
   the filters, gift finder, drop-a-hint, sticky-chip highlight, popovers, scroll reveals and click-to-load video. */
(() => {
  if (window.ggInit) return;
  window.ggInit = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const track = (name, detail) => {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail);
    } catch (e) {}
  };

  /* Sticky offset: keep the chip bar below Dawn's sticky header. */
  const setStickyOffset = () => {
    const header = document.querySelector('.section-header.shopify-section-header-sticky, sticky-header.header-wrapper');
    let h = 0;
    const sticky = document.querySelector('.shopify-section-header-sticky');
    if (sticky && !sticky.classList.contains('shopify-section-header-hidden')) h = sticky.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--gg-sticky-offset', `${Math.max(0, Math.round(h))}px`);
    return header;
  };

  /* ---------- Chip nav: highlight the section in view, "Shop by moment" dropdown, mobile panel ---------- */
  const initNav = (nav) => {
    if (nav.dataset.ggReady) return;
    nav.dataset.ggReady = 'true';
    const links = [...nav.querySelectorAll('a.gg-chip[href^="#"]')];
    const toggles = [...nav.querySelectorAll('[data-gg-toggle]')];
    const current = nav.querySelector('[data-gg-nav-current]');
    const moreToggle = nav.querySelector('.gg-nav__more-toggle');
    const moreLabel = nav.querySelector('[data-gg-more-label]');
    const morePanel = nav.querySelector('.gg-nav__more-panel');

    const panelOf = (t) => document.getElementById(t.getAttribute('aria-controls'));
    const closeAll = (except) => toggles.forEach((t) => {
      if (t === except) return;
      t.setAttribute('aria-expanded', 'false');
      const p = panelOf(t);
      if (p) p.hidden = true;
    });
    toggles.forEach((t) => t.addEventListener('click', () => {
      const open = t.getAttribute('aria-expanded') !== 'true';
      closeAll(t);
      t.setAttribute('aria-expanded', open ? 'true' : 'false');
      const p = panelOf(t);
      if (p) p.hidden = !open;
    }));
    document.addEventListener('click', (e) => { if (!nav.contains(e.target)) closeAll(); });
    nav.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = toggles.find((t) => t.getAttribute('aria-expanded') === 'true');
      if (open) { closeAll(); open.focus(); }
    });

    // One target section can have two links (desktop pill + mobile panel).
    const map = new Map();
    links.forEach((a) => {
      const el = document.getElementById(a.getAttribute('href').slice(1));
      // A chip whose section isn't on the page (e.g. the dated fundraiser band before Oct 9) hides itself.
      if (!el) { a.hidden = true; return; }
      map.set(el, [...(map.get(el) || []), a]);
      a.addEventListener('click', () => {
        closeAll();
        track('gg_chip_click', { label: a.textContent.trim() });
      });
    });
    if (!map.size || !('IntersectionObserver' in window)) return;
    let active = null;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const group = map.get(e.target);
          if (!group || group === active) return;
          if (active) active.forEach((a) => a.removeAttribute('aria-current'));
          group.forEach((a) => a.setAttribute('aria-current', 'true'));
          active = group;
          const label = group[0].textContent.trim();
          if (current) current.textContent = label;
          // When the section lives in the dropdown, the dropdown pill shows it and lights up.
          if (moreToggle && moreLabel) {
            const inMore = morePanel && group.some((a) => morePanel.contains(a));
            moreLabel.textContent = inMore ? label : moreLabel.dataset.default;
            if (inMore) moreToggle.setAttribute('aria-current', 'true');
            else moreToggle.removeAttribute('aria-current');
          }
        });
      },
      { rootMargin: '-35% 0px -60% 0px' }
    );
    map.forEach((_, el) => io.observe(el));
  };

  /* ---------- Back to the guide ([data-gg-back]) ----------
     On gift guide collections, "← Gift Guide" links point at the guide's band for this collection
     (the category nav carries it as data-gg-current-anchor). If the visitor came from the guide,
     step back in history instead, so they land exactly where they left off. */
  const initBack = () => {
    const nav = document.querySelector('[data-gg-current-anchor]');
    const anchor = nav ? nav.dataset.ggCurrentAnchor : '';
    document.querySelectorAll('a[data-gg-back]').forEach((a) => {
      if (a.dataset.ggBackReady) return;
      a.dataset.ggBackReady = 'true';
      const url = new URL(a.href, location.href);
      if (anchor && !url.hash) { url.hash = anchor; a.href = url.href; }
      a.addEventListener('click', (e) => {
        track('gg_back_to_guide', { from: location.pathname });
        let ref = null;
        try { ref = document.referrer ? new URL(document.referrer) : null; } catch (err) {}
        if (ref && ref.origin === location.origin && ref.pathname === url.pathname && history.length > 1) {
          e.preventDefault();
          history.back();
        }
      });
    });
  };

  /* ---------- Filters: fetch matches from the WHOLE collection on tap ----------
     Chips request /collections/<handle>[/<tag>]?view=gg-<range>. Those views are layout-less
     collection templates, so the response is just the server-rendered theme cards. "All" restores the curated picks. Responses are
     cached per URL. If a request fails, falls back to filtering the cards already on the page. */
  const filterCache = new Map();
  const loadResults = (url) => {
    if (!filterCache.has(url)) {
      filterCache.set(url, fetch(url).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); }));
      filterCache.get(url).catch(() => filterCache.delete(url));
    }
    return filterCache.get(url);
  };
  const parseResults = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const meta = doc.querySelector('[data-gg-results-count]');
    const items = [...doc.querySelectorAll('[data-gg-results-list] > li')];
    return { items, count: Number(meta ? meta.dataset.ggResultsCount : items.length) };
  };
  const resultsUrl = (handle, tags, view) => {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const tagPath = tags && tags.length ? '/' + tags.map(encodeURIComponent).join('+') : '';
    return `${root}collections/${handle}${tagPath}?view=${view}`;
  };
  // Trim a result list to whole grid rows so no card sits alone (keeps at least one row).
  const fullRows = (grid, items) => {
    const cols = (getComputedStyle(grid).gridTemplateColumns || '').split(' ').filter(Boolean).length || 1;
    if (items.length <= cols) return items;
    return items.slice(0, items.length - (items.length % cols));
  };

  const initFilter = (section) => {
    if (section.dataset.ggFilterReady) return;
    section.dataset.ggFilterReady = 'true';
    const chips = [...section.querySelectorAll('[data-gg-price-chip]')];
    const grid = section.querySelector('.gg-grid');
    const status = section.querySelector('[data-gg-filter-status]');
    const handle = section.dataset.ggCollection;
    const linkMode = section.dataset.ggMode === 'link';
    if (!chips.length || !grid) return;
    const originals = [...grid.children];
    let token = 0;

    const setStatus = (chip, count, shown) => {
      if (!status) return;
      if (chip.dataset.all === 'true') { status.textContent = ''; return; }
      if (!count) { status.textContent = status.dataset.empty || 'Nothing here yet'; return; }
      const label = chip.dataset.label.toLowerCase();
      const tpl = shown < count ? (status.dataset.templateMore || status.dataset.template) : status.dataset.template;
      status.textContent = (tpl || 'Showing [count] [label]').replace('[shown]', shown).replace('[count]', count).replace('[label]', label);
    };
    const restore = () => {
      grid.querySelectorAll('[data-gg-fetched]').forEach((li) => li.remove());
      originals.forEach((li) => { li.hidden = false; });
    };
    const localFilter = (chip) => {
      const min = Number(chip.dataset.min || 0);
      const max = Number(chip.dataset.max || Infinity);
      const tag = chip.dataset.tag;
      let n = 0;
      originals.forEach((li) => {
        if (!li.dataset.ggPrice) { li.hidden = true; return; }
        const p = Number(li.dataset.ggPrice);
        const ok = tag ? (li.dataset.ggTags || '').includes(` ${tag} `) : p >= min && p < max;
        li.hidden = !ok;
        if (ok) n++;
      });
      setStatus(chip, n, n);
    };

    const urlFor = (chip) => resultsUrl(handle, chip.dataset.tag ? [chip.dataset.tag] : [], chip.dataset.view);
    const load = loadResults;
    // Warm the cache on hover/focus so the tap feels instant.
    chips.forEach((chip) => {
      if (chip.dataset.all === 'true' || !handle || !chip.dataset.view) return;
      const warm = () => load(urlFor(chip));
      chip.addEventListener('pointerenter', warm, { once: true });
      chip.addEventListener('focus', warm, { once: true });
      chip.addEventListener('touchstart', warm, { once: true, passive: true });
    });

    chips.forEach((chip) => chip.addEventListener('click', async () => {
      chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      track('gg_price_filter', { section: section.id, label: chip.dataset.label });
      const mine = ++token;
      if (chip.dataset.all === 'true') { restore(); setStatus(chip); return; }
      if (!handle || !chip.dataset.view) { restore(); localFilter(chip); return; }

      const url = urlFor(chip);
      grid.setAttribute('aria-busy', 'true');
      grid.classList.add('gg-grid--loading');
      try {
        const html = await load(url);
        if (mine !== token) return;
        const { items: all, count } = parseResults(html);
        restore();
        originals.forEach((li) => { li.hidden = true; });
        const items = fullRows(grid, all);
        items.forEach((li, i) => {
          if (linkMode) li.querySelectorAll('.quick-add').forEach((q) => q.remove());
          li.setAttribute('data-gg-fetched', '');
          grid.appendChild(popIn(document.importNode(li, true), i));
        });
        setStatus(chip, count, items.length);
      } catch (e) {
        filterCache.delete(url);
        if (mine === token) { restore(); localFilter(chip); }
      } finally {
        if (mine === token) {
          grid.removeAttribute('aria-busy');
          grid.classList.remove('gg-grid--loading');
        }
      }
    }));
  };

  /* ---------- Gift finder: Who → What → Budget → results ----------
     Driven by a small JSON index of every gift (collection view gg-index): each answer shows a live
     count, results are ranked (best-sellers first, varied brands), led by a "top pick" (a staff pick
     on this page when one matches), with Show more, Shuffle and Surprise me. Cards are rendered by the
     theme (/products/<handle>?view=gg-card) so they keep badges, cashback and quick add.
     Empty combos relax "Who" first, then the budget, and the status line says what changed. */
  const SETRX = /\bset\b|kit\b|collection|\bbox\b|\btin\b|assorted|pack of|bundle|deluxe|limited|gift/i;
  const indexCache = new Map();
  const loadIndex = (handle) => {
    if (indexCache.has(handle)) return indexCache.get(handle);
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const page = (n) => fetch(`${root}collections/${handle}?view=gg-index&page=${n}`)
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then((t) => JSON.parse(t.slice(t.indexOf('{'), t.lastIndexOf('}') + 1)));
    const p = page(1).then(async (first) => {
      const rest = await Promise.all(Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => page(i + 2).catch(() => ({ items: [] }))));
      return [first, ...rest].flatMap((x) => x.items).filter((x) => x.a).map((x, i) => ({ ...x, rank: i, tags: ` ${x.g} `, isSet: SETRX.test(x.t) }));
    });
    indexCache.set(handle, p);
    p.catch(() => indexCache.delete(handle));
    return p;
  };
  const cardCache = new Map();
  const loadCard = (handle) => {
    if (!cardCache.has(handle)) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      cardCache.set(handle, fetch(`${root}products/${handle}?view=gg-card`).then((r) => r.text()).then((html) => {
        const li = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gg-results-list] > li');
        return li ? document.importNode(li, true) : null;
      }).catch(() => null));
    }
    return cardCache.get(handle).then((li) => (li ? li.cloneNode(true) : null));
  };
  const countUp = (el, to) => {
    const from = Number(el.dataset.n || 0);
    el.dataset.n = to;
    if (reduceMotion() || from === to) { el.textContent = to; return; }
    const t0 = performance.now();
    const step = (t) => {
      const k = Math.min(1, (t - t0) / 450);
      el.textContent = Math.round(from + (to - from) * (1 - Math.pow(1 - k, 3)));
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    setTimeout(() => { el.textContent = to; }, 520); // rAF pauses in background tabs
  };
  const phrase = (...bits) => bits.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();

  const initFinder = (section) => {
    if (section.dataset.ggFinderReady) return;
    section.dataset.ggFinderReady = 'true';
    const steps = ['who', 'what', 'budget'];
    const stepEl = (k) => section.querySelector(`[data-gg-step="${k}"]`);
    const dots = [...section.querySelectorAll('[data-gg-step-dot]')];
    const back = section.querySelector('[data-gg-finder-back]');
    const grid = section.querySelector('[data-gg-finder-grid]');
    const status = section.querySelector('[data-gg-finder-status]');
    const title = section.querySelector('[data-gg-finder-title]');
    const seeAll = section.querySelector('[data-gg-finder-all]');
    const moreBtn = section.querySelector('[data-gg-finder-more]');
    const seeAllBase = seeAll ? seeAll.getAttribute('href') : '';
    const handle = section.dataset.ggCollection;
    const topLabel = section.dataset.ggTopLabel || 'Our top pick';
    const answers = {};
    let current = 'who';
    let token = 0;
    let pool = [];      // ranked matches for the current answers
    let shown = 0;      // how many of the pool are on screen
    let note = '';
    let index = null;
    let topWhy = '';
    const PAGE = 5;     // top pick (full-width, horizontal) + one row of 4

    // Staff picks on this page (tiles + Staff Picks rail) → quote for the top pick.
    const staff = new Map();
    document.querySelectorAll('[data-gg-staff]').forEach((el) => {
      if (!staff.has(el.dataset.ggStaff)) staff.set(el.dataset.ggStaff, { quote: el.dataset.ggStaffQuote, who: el.dataset.ggStaffWho });
    });

    const tagsOf = (opt) => (opt && opt.dataset.tags ? opt.dataset.tags.split(',').map((t) => t.trim()).filter(Boolean) : []);
    const has = (item, tag) => item.tags.includes(` ${tag} `);
    const matchWho = (item, who) => !who.length || who.every((t) => has(item, t));
    const matchWhat = (item, what) => !what.length || what.some((t) => has(item, t));
    const matchBudget = (item, b) => !b || ((!b.min || item.p >= b.min) && (!b.max || item.p < b.max));
    const budgetOf = (opt) => (opt ? { min: Number(opt.dataset.min || 0), max: Number(opt.dataset.max || 0) } : null);

    const filter = (who, what, budget) => (index || []).filter((x) => matchWho(x, who) && matchWhat(x, what) && matchBudget(x, budget));
    const sel = (k) => section.querySelector(`[data-gg-answer="${k}"][aria-pressed="true"]`);

    // Live counts on the options of the step that's showing.
    const paintCounts = (key) => {
      if (!index) return;
      const who = tagsOf(sel('who'));
      const what = tagsOf(sel('what'));
      section.querySelectorAll(`[data-gg-answer="${key}"]`).forEach((opt) => {
        let n;
        if (key === 'who') n = filter(tagsOf(opt), [], null).length;
        else if (key === 'what') n = filter(who, tagsOf(opt), null).length;
        else n = filter(who, what, budgetOf(opt)).length;
        const c = opt.querySelector('[data-gg-count]');
        if (c) { countUp(c, n); c.title = `${n} gifts`; }
        opt.classList.toggle('gg-finder__opt--empty', n === 0);
      });
    };
    const warm = () => loadIndex(handle).then((ix) => { index = ix; paintCounts(current); }).catch(() => {});

    const show = (key, focus = true) => {
      current = key;
      ['who', 'what', 'budget', 'results'].forEach((k) => { const el = stepEl(k); if (el) el.hidden = k !== key; });
      section.querySelectorAll('[data-gg-step-extra]').forEach((el) => { el.hidden = el.dataset.ggStepExtra !== key; });
      const idx = steps.indexOf(key);
      dots.forEach((d, i) => {
        d.toggleAttribute('data-done', idx === -1 || i < idx);
        if (i === idx) d.setAttribute('aria-current', 'step'); else d.removeAttribute('aria-current');
      });
      if (back) back.hidden = key === 'who';
      const el = stepEl(key);
      if (el && !reduceMotion()) { el.classList.remove('gg-step-in'); void el.offsetWidth; el.classList.add('gg-step-in'); }
      if (key !== 'results') paintCounts(key);
      if (focus) {
        const target = key === 'results' ? title : el && el.querySelector('.gg-finder__opt');
        if (target) target.focus({ preventScroll: true });
      }
    };

    // Rank: best-sellers first (index order), sets nudged up, no more than 2 of a brand per page.
    const rankPool = (items, shuffle) => {
      let list = items.map((x) => ({ x, s: x.rank - (x.isSet ? 40 : 0) - (staff.has(x.h) ? 60 : 0) + (shuffle ? Math.random() * 220 : 0) }))
        .sort((a, b) => a.s - b.s).map((o) => o.x);
      const out = [];
      const vend = new Map();
      const later = [];
      list.forEach((x) => {
        const page = Math.floor(out.length / PAGE);
        const key = `${page}|${x.b}`;
        if ((vend.get(key) || 0) >= 2) { later.push(x); return; }
        vend.set(key, (vend.get(key) || 0) + 1);
        out.push(x);
      });
      return out.concat(later);
    };
    // Top pick: a staff pick if one matches, else the strongest set in the upper half of the price range.
    const pickTop = (list) => {
      const sp = list.slice(0, 30).find((x) => staff.has(x.h));
      if (sp) return sp;
      const prices = list.map((x) => x.p).sort((a, b) => a - b);
      const mid = prices[Math.floor(prices.length / 2)] || 0;
      return list.slice(0, 20).find((x) => x.isSet && x.p >= mid) || list[0];
    };

    const renderBatch = async (items, mine, first) => {
      const cards = await Promise.all(items.map((x) => loadCard(x.h)));
      if (mine !== token) return;
      cards.forEach((li, i) => {
        if (!li) return;
        const x = items[i];
        if (first && i === 0) {
          li.classList.add('gg-finder__top');
          const s = staff.get(x.h);
          const tag = document.createElement('div');
          tag.className = 'gg-finder__toptag';
          tag.innerHTML = `<span class="gg-finder__toplabel">★ ${topLabel}</span>`;
          const q = document.createElement('p');
          if (s && s.quote) {
            q.className = 'gg-finder__topquote';
            q.textContent = `“${s.quote}” — ${s.who}`;
          } else {
            q.className = 'gg-finder__topwhy';
            q.textContent = topWhy || 'One of our best-selling gifts.';
          }
          tag.appendChild(q);
          li.prepend(tag);
        }
        grid.appendChild(popIn(li, i + (first ? 0 : 1)));
      });
    };

    const showMore = async () => {
      const mine = token;
      const next = pool.slice(shown, shown + (shown ? 4 : PAGE));
      const first = shown === 0;
      shown += next.length;
      moreBtn.hidden = true;
      await renderBatch(next, mine, first);
      if (mine !== token) return;
      moreBtn.hidden = shown >= pool.length;
    };

    const run = async ({ shuffle = false, surprise = false } = {}) => {
      const mine = ++token;
      show('results');
      grid.innerHTML = '';
      grid.setAttribute('aria-busy', 'true');
      moreBtn.hidden = true;
      status.textContent = 'Finding gifts…';
      grid.classList.add('gg-grid--loading');
      try {
        index = await loadIndex(handle);
        if (mine !== token) return;
        const whoOpt = sel('who');
        const whatOpt = sel('what');
        const budOpt = sel('budget');
        const who = tagsOf(whoOpt);
        const what = tagsOf(whatOpt);
        const budget = budgetOf(budOpt);
        const whoLabel = whoOpt ? whoOpt.dataset.label : '';
        const whoPhrase = whoOpt ? (whoOpt.dataset.phrase || `for a ${whoLabel.toLowerCase()}`) : '';
        const whatLabel = whatOpt ? whatOpt.dataset.label : '';
        const budLabel = budOpt ? budOpt.dataset.label : '';
        topWhy = whatLabel && !surprise ? `One of our best-selling ${whatLabel.toLowerCase()} gifts.` : 'One of our best-selling gifts.';
        let list = filter(who, what, budget);
        note = '';
        if (!surprise && list.length < 4 && who.length) {
          const relaxed = filter([], what, budget);
          if (relaxed.length > list.length) { list = relaxed; note = `${phrase('Few', whatLabel.toLowerCase(), 'gifts', whoPhrase, budLabel, 'yet, so we\'ve added our favourite', whatLabel.toLowerCase(), 'gifts', budLabel)}.`; }
        }
        if (!surprise && list.length < 4 && budget) {
          const relaxed = filter([], what, null);
          if (relaxed.length > list.length) { list = relaxed; note = `${phrase('Nothing', budLabel, 'right now, so here are our top', whatLabel.toLowerCase(), 'gifts at any price')}.`; }
        }
        let ranked = rankPool(list, shuffle || surprise);
        const top = pickTop(ranked);
        if (top) ranked = [top, ...ranked.filter((x) => x !== top)];
        pool = ranked;
        shown = 0;
        await showMore();
        const parts = surprise ? 'a little surprise from our team' : [whatLabel, whoLabel && who.length ? whoPhrase : '', budLabel].filter(Boolean).join(', ');
        status.textContent = pool.length
          ? (note || `${pool.length} gift${pool.length === 1 ? '' : 's'} match: ${parts}`)
          : 'We couldn\'t find a match. Try a different answer, or browse the guide below.';
        if (seeAll) {
          const sets = what.length ? what.map((w) => [...who, w]) : [who];
          seeAll.href = sets.length === 1 && sets[0].length ? resultsUrl(handle, sets[0], '').replace('?view=', '') : seeAllBase;
        }
        track('gg_finder_results', { who: whoLabel, what: whatLabel, budget: budLabel, results: pool.length, shuffle, surprise });
      } catch (e) {
        if (mine === token) status.textContent = 'Something went wrong loading gifts. Please try again.';
      } finally {
        if (mine === token) { grid.removeAttribute('aria-busy'); grid.classList.remove('gg-grid--loading'); }
      }
    };

    section.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-gg-answer]');
      if (opt) {
        const key = opt.dataset.ggAnswer;
        opt.parentElement.querySelectorAll('[data-gg-answer]').forEach((o) => o.setAttribute('aria-pressed', o === opt ? 'true' : 'false'));
        answers[key] = opt.dataset.label;
        track('gg_finder_answer', { step: key, label: opt.dataset.label });
        if (key === 'budget') run(); else show(steps[steps.indexOf(key) + 1]);
        return;
      }
      if (e.target.closest('[data-gg-surprise]')) {
        section.querySelectorAll('[data-gg-answer]').forEach((o) => o.removeAttribute('aria-pressed'));
        run({ surprise: true });
        return;
      }
      if (e.target.closest('[data-gg-finder-more]')) { showMore(); return; }
      if (e.target.closest('[data-gg-finder-shuffle]')) { run({ shuffle: true }); return; }
      if (e.target.closest('[data-gg-finder-back]')) {
        const idx = current === 'results' ? steps.length - 1 : steps.indexOf(current) - 1;
        show(steps[Math.max(0, idx)]);
        return;
      }
      if (e.target.closest('[data-gg-finder-restart]')) {
        Object.keys(answers).forEach((k) => delete answers[k]);
        section.querySelectorAll('[data-gg-answer]').forEach((o) => o.removeAttribute('aria-pressed'));
        show('who');
        section.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    });

    // Load the index as the finder approaches the viewport (or on first touch).
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) { io.disconnect(); warm(); } }, { rootMargin: '600px 0px' });
      io.observe(section);
    } else warm();
    section.addEventListener('pointerenter', warm, { once: true });
    show('who', false);
  };

  /* ---------- Gift Tags: kraft-tag tabs that swap one product rail ----------
     Real tabs (role="tab", roving tabindex, arrow/Home/End keys, automatic activation). The first tag
     is server-rendered; others fetch each of their collections through the gg-all view and merge them
     round-robin without repeats. Responses share the filter cache and are warmed on hover/focus. */
  const initTags = (section) => {
    if (section.dataset.ggTagsReady) return;
    section.dataset.ggTagsReady = 'true';
    const tabs = [...section.querySelectorAll('[data-gg-tag]')];
    const panel = section.querySelector('[role="tabpanel"]');
    const rail = section.querySelector('[data-gg-tags-rail]');
    const status = section.querySelector('[data-gg-tags-status]');
    const foot = section.querySelector('[data-gg-tags-foot]');
    const all = section.querySelector('[data-gg-tags-all]');
    const linkMode = section.dataset.ggMode === 'link';
    if (!tabs.length || !rail) return;
    const firstItems = [...rail.children];
    const cache = new Map();
    let token = 0;

    const handlesOf = (tab) => (tab.dataset.collections || '').split(',').filter(Boolean);
    const urlsOf = (tab) => handlesOf(tab).map((h) => resultsUrl(h, [], 'gg-all'));
    const warm = (tab) => urlsOf(tab).forEach((u) => loadResults(u).catch(() => {}));
    const itemsFor = async (tab) => {
      if (tab === tabs[0]) return firstItems;
      if (cache.has(tab)) return cache.get(tab);
      const limit = Number(tab.dataset.limit || 8);
      const pages = await Promise.all(urlsOf(tab).map((u) => loadResults(u).then(parseResults).catch(() => ({ items: [] }))));
      const seen = new Set();
      const merged = [];
      for (let i = 0; merged.length < limit && pages.some((p) => i < p.items.length); i++) {
        pages.forEach((p) => {
          const li = p.items[i];
          if (!li || merged.length >= limit) return;
          const a = li.querySelector('a[href*="/products/"]');
          const key = a ? a.getAttribute('href').split('?')[0] : li.textContent;
          if (seen.has(key)) return;
          seen.add(key);
          if (linkMode) li.querySelectorAll('.quick-add').forEach((q) => q.remove());
          merged.push(li);
        });
      }
      cache.set(tab, merged);
      return merged;
    };
    const setStatus = (tab, n) => {
      if (!status) return;
      const tpl = status.dataset.template || 'Gifts for [label]';
      status.textContent = n === 0 ? `Nothing here yet for ${tab.dataset.label}. Try another tag.` : tpl.replace('[label]', tab.dataset.label);
    };

    const select = async (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      if (focus) tab.focus();
      panel.setAttribute('aria-labelledby', tab.id);
      if (all && foot) {
        all.href = tab.dataset.allUrl || '#';
        all.textContent = tab.dataset.allLabel || '';
        foot.hidden = !tab.dataset.allUrl || !tab.dataset.allLabel;
      }
      track('gg_gift_tag', { label: tab.dataset.label });
      const mine = ++token;
      rail.setAttribute('aria-busy', 'true');
      rail.classList.add('gg-grid--loading');
      try {
        const items = await itemsFor(tab);
        if (mine !== token) return;
        rail.replaceChildren(...items.map((li, i) => popIn(tab === tabs[0] ? li : document.importNode(li, true), i)));
        rail.scrollLeft = 0;
        rail.dispatchEvent(new Event('scroll'));
        setStatus(tab, items.length);
      } finally {
        if (mine === token) { rail.removeAttribute('aria-busy'); rail.classList.remove('gg-grid--loading'); }
      }
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => { if (tab.getAttribute('aria-selected') !== 'true') select(tab, false); });
      tab.addEventListener('pointerenter', () => warm(tab), { once: true });
      tab.addEventListener('focus', () => warm(tab), { once: true });
      tab.addEventListener('keydown', (e) => {
        const keys = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: tabs.length - 1 };
        if (!(e.key in keys)) return;
        e.preventDefault();
        const next = tabs[(keys[e.key] + tabs.length) % tabs.length];
        select(next, true);
      });
    });
  };

  /* ---------- Bookshelf: shelf dropdown + unfolding spotlight ----------
     Tapping a cover pulls the book off the shelf and unfolds its shelf-talker card directly under
     that book's row (between shelves when there are two), with a tab pointing up at the book.
     Same row: the card stays open, its tab slides and the content swaps. Tapping the open book or
     × folds it away. Every book's card content is pre-rendered, so it's instant. Without JS the card
     simply sits after the shelves showing the first book. */
  const initBookshelf = (section) => {
    if (section.dataset.ggShelvesReady) return;
    section.dataset.ggShelvesReady = 'true';
    const select = section.querySelector('[data-gg-shelf-select]');
    const picks = [...section.querySelectorAll('[data-gg-book]')];
    const items = [...section.querySelectorAll('[data-gg-spot]')];
    const panel = section.querySelector('[data-gg-spot-panel]');
    if (!panel || !picks.length) return;
    panel.classList.add('gg-spot--js');
    const slot = document.createElement('li');
    slot.className = 'gg-spot-slot';
    slot.appendChild(panel);
    let current = null;
    let open = false;

    const rowEnd = (li) => {
      // Last visible card in the same grid row as li
      const ul = li.parentElement;
      const top = li.offsetTop;
      const row = [...ul.children].filter((c) => c !== slot && c.offsetParent !== null && Math.abs(c.offsetTop - top) < 4);
      return row[row.length - 1] || li;
    };
    const aim = (pick) => {
      const li = pick.closest('li');
      const cover = pick.querySelector('.gg-book__cover') || li;
      const r = cover.getBoundingClientRect();
      const p = panel.getBoundingClientRect();
      const x = Math.max(28, Math.min(p.width - 28, r.left + r.width / 2 - p.left));
      panel.style.setProperty('--gg-notch-x', `${x}px`);
    };
    const replay = (cls) => {
      panel.classList.remove('is-unfolding', 'is-folding');
      if (reduceMotion()) return;
      void panel.offsetWidth;
      panel.classList.add(cls);
    };
    const place = (pick, animate) => {
      const end = rowEnd(pick.closest('li'));
      const moved = slot.previousElementSibling !== end || !slot.isConnected;
      if (moved) end.after(slot);
      aim(pick);
      if (animate && (moved || !open)) replay('is-unfolding');
    };
    const spotlight = (pick, { animate = true, scroll = true } = {}) => {
      const id = pick.dataset.ggBook;
      const item = items.find((i) => i.dataset.ggSpot === id);
      if (!item) return;
      picks.forEach((p) => p.setAttribute('aria-pressed', p === pick ? 'true' : 'false'));
      const swap = open && current !== pick;
      items.forEach((i) => { i.hidden = i !== item; });
      if (swap && !reduceMotion()) { item.classList.remove('gg-step-in'); void item.offsetWidth; item.classList.add('gg-step-in'); }
      slot.hidden = false;
      place(pick, animate);
      current = pick;
      open = true;
      if (scroll) {
        // Measure the slot (untransformed) so the unfold animation doesn't skew the numbers. Bring the
        // whole card into view, but never push the chosen book up under the sticky header.
        const r = slot.getBoundingClientRect();
        const bookTop = pick.getBoundingClientRect().top;
        const need = r.bottom - window.innerHeight + 24;
        if (need > 0) window.scrollBy({ top: Math.min(need, bookTop - 140), behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    };
    const fold = () => {
      if (!open) return;
      picks.forEach((p) => p.setAttribute('aria-pressed', 'false'));
      open = false;
      const done = () => { if (!open) slot.hidden = true; };
      if (reduceMotion()) return done();
      replay('is-folding');
      panel.addEventListener('animationend', done, { once: true });
      if (current) current.focus({ preventScroll: true });
    };

    picks.forEach((p) => p.addEventListener('click', () => {
      if (open && current === p) { fold(); return; }
      spotlight(p);
      track('gg_book_spotlight', { book: (p.querySelector('.gg-book__title') || p).textContent.trim() });
    }));
    panel.querySelector('[data-gg-spot-close]')?.addEventListener('click', fold);
    section.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) fold(); });

    const firstVisible = () => picks.find((p) => p.offsetParent !== null);
    if (select) {
      const groups = [...select.options].map((o) => document.getElementById(o.value)).filter(Boolean);
      select.addEventListener('change', () => {
        groups.forEach((g) => { g.hidden = g.id !== select.value; });
        const shown = document.getElementById(select.value);
        if (shown) {
          shown.querySelectorAll('.gg-book').forEach((li, i) => popIn(li, i));
          const first = shown.querySelector('[data-gg-book]');
          if (first) spotlight(first, { scroll: false });
        }
        track('gg_bookshelf', { shelf: select.options[select.selectedIndex].text });
      });
    }

    // Start with the first book's card open under the first row (no animation on load).
    const start = picks.find((p) => p.getAttribute('aria-pressed') === 'true') || firstVisible();
    if (start) spotlight(start, { animate: false, scroll: false });

    // Rows reflow on resize: keep the card under the right row and the tab on the book.
    let rt;
    window.addEventListener('resize', () => {
      clearTimeout(rt);
      rt = setTimeout(() => { if (open && current) place(current, false); }, 120);
    }, { passive: true });

    // Theme editor: selecting a book block shows its shelf and spotlights it.
    section.addEventListener('shopify:block:select', (e) => {
      const pick = section.querySelector(`[data-gg-book="${e.detail.blockId}"]`);
      if (!pick) return;
      const group = pick.closest('.gg-shelf-group');
      if (select && group && group.hidden) { select.value = group.id; select.dispatchEvent(new Event('change')); }
      spotlight(pick, { scroll: false });
    });
  };

  /* ---------- Drop a hint: share sheet on phones, copy + email on desktop ---------- */
  let toastTimer;
  const toast = (html) => {
    let t = document.querySelector('.gg-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'gg-toast';
      t.setAttribute('role', 'status');
      document.body.appendChild(t);
    }
    t.innerHTML = html;
    t.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('is-on'), 5000);
  };
  const dropHint = async (btn) => {
    const cfg = window.ggHint || {};
    const message = cfg.message || 'Hint, hint 🎨 This is on my wish list from Opus:';
    const subject = cfg.subject || 'A little gift hint 🎁';
    const url = btn.dataset.url;
    const text = `${message} ${btn.dataset.title}`;
    track('gg_drop_hint', { product: btn.dataset.title });
    if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
      try { await navigator.share({ title: btn.dataset.title, text, url }); } catch (e) {}
      return;
    }
    const mail = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${text}\n${url}`)}`;
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast(`<span>Hint copied, paste it anywhere.</span> <a href="${mail}">Email it instead</a>`);
    } catch (e) {
      window.location.href = mail;
    }
  };

  /* ---------- Scroll moments: script accents paint in, headings and cards rise in ----------
     Only elements still below the fold at load get the hidden start state, so nothing visible
     ever flashes. Off entirely with reduced motion. */
  const popIn = (el, i) => {
    if (!reduceMotion()) {
      el.classList.add('gg-pop-in');
      el.style.animationDelay = `${Math.min(i, 8) * 45}ms`;
    }
    return el;
  };
  const initReveal = () => {
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    const targets = [
      ...document.querySelectorAll('.gg .gg-accent, .gg .gg-head__text, .gg .gg-grid > li, .gg .gg-feature__media, .gg .gg-rail > li, .gg .gg-stuff__map'),
    ];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-inview');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    const fold = window.innerHeight;
    const pending = new Set();
    targets.forEach((el) => {
      if (el.getBoundingClientRect().top < fold) return;
      const sib = el.parentElement ? [...el.parentElement.children].indexOf(el) : 0;
      if (el.matches('.gg-grid > li, .gg-rail > li')) el.style.transitionDelay = `${(sib % 4) * 70}ms`;
      el.classList.add('gg-reveal');
      pending.add(el);
      io.observe(el);
    });
    // Backup: if IntersectionObserver ever stalls (some in-app browsers), never leave content hidden.
    let t;
    const sweep = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        pending.forEach((el) => {
          if (el.classList.contains('is-inview')) { pending.delete(el); return; }
          if (el.getBoundingClientRect().top < window.innerHeight * 0.95) { el.classList.add('is-inview'); pending.delete(el); }
        });
        if (!pending.size) window.removeEventListener('scroll', sweep);
      }, 150);
    };
    window.addEventListener('scroll', sweep, { passive: true });
  };

  /* ---------- Click-to-load video ---------- */
  // Bookshelf "Add both": one /cart/add.js call for the book + its pairing, then refresh the
  // Opus cart drawer the same way Dawn's product-form does (falls back to the cart page).
  const addBoth = async (btn) => {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    const ids = btn.dataset.ggAddBoth.split(',').filter(Boolean);
    const msg = btn.parentElement.querySelector('.gg-both__msg');
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('loading');
    try {
      const body = { items: ids.map((id) => ({ id: Number(id), quantity: 1 })) };
      if (cart && cart.getSectionsToRender) {
        body.sections = cart.getSectionsToRender().map((s) => s.section || s.id);
        body.sections_url = window.location.pathname;
      }
      const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add');
      if (msg) msg.textContent = btn.dataset.done || 'Added';
      track('gg_add_both', { ids: ids.join(',') });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-guide-add-both', cartData: data });
      if (cart && cart.tagName === 'CART-DRAWER' && cart.renderContents && data.sections) {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(data);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
    } catch (err) {
      if (msg) msg.textContent = err.message;
    } finally {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading');
    }
  };

  // Make it Merry card wall: one card selected at a time; its supplies panel shows below.
  const initXcards = (section) => {
    if (section.dataset.ggXcardsReady) return;
    section.dataset.ggXcardsReady = 'true';
    const tabs = [...section.querySelectorAll('[data-gg-xcard]')];
    const panels = [...section.querySelectorAll('[data-gg-xpanel]')];
    const select = (id, focus) => {
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.ggXcard === id)));
      panels.forEach((p) => {
        const on = p.dataset.ggXpanel === id;
        p.hidden = !on;
        if (on && !reduceMotion()) { p.classList.remove('gg-step-in'); void p.offsetWidth; p.classList.add('gg-step-in'); }
      });
      if (focus) {
        const panel = panels.find((p) => p.dataset.ggXpanel === id);
        if (panel && panel.getBoundingClientRect().top > window.innerHeight * 0.75) panel.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'nearest' });
      }
      track('gg_card_pick', { id });
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(t.dataset.ggXcard, true));
      t.addEventListener('keydown', (e) => {
        const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        const next = tabs[(i + d + tabs.length) % tabs.length];
        next.focus(); select(next.dataset.ggXcard, false);
      });
    });
    document.addEventListener('shopify:block:select', (e) => {
      if (section.contains(e.target) && e.target.dataset.ggXcard) select(e.target.dataset.ggXcard, false);
    });
  };

  // Cashback chip sits beside the price inside the theme's card (core card-product is untouched;
  // the chip is rendered after it by gg-card and moved in here). Runs for cards added later too.
  const placeCashback = (root = document) => {
    root.querySelectorAll('.gg-card-wrap > .gg-cb').forEach((cb) => {
      const price = cb.parentElement.querySelector('.card-information .price');
      if (!price) return;
      const row = document.createElement('div');
      row.className = 'gg-price-row';
      price.parentNode.insertBefore(row, price);
      row.append(price, cb);
      cb.classList.add('gg-cb--inline');
    });
  };
  // "?" cashback explainer: one open at a time, closes on outside click or Escape.
  // The pop-over is lifted to <body> while open so the hero's overflow/stacking can't hide it:
  // a sheet pinned to the bottom of the screen on phones, a floating card under the "?" on desktop.
  const helpHome = new WeakMap();
  const popOf = (b) => (b._ggPop = b._ggPop || b.parentElement.querySelector('.gg-help__pop'));
  const closeHelps = (except) => document.querySelectorAll('[data-gg-help-toggle][aria-expanded="true"]').forEach((b) => {
    if (b === except) return;
    b.setAttribute('aria-expanded', 'false');
    const pop = popOf(b);
    if (!pop) return;
    pop.hidden = true;
    pop.classList.remove('gg-help__pop--float');
    const home = helpHome.get(pop);
    if (home && pop.parentElement !== home) home.appendChild(pop);
  });
  const openHelp = (b) => {
    const pop = popOf(b);
    if (!pop) return;
    if (!helpHome.has(pop)) helpHome.set(pop, pop.parentElement);
    document.body.appendChild(pop);
    pop.classList.add('gg-help__pop--float');
    pop.hidden = false;
    b.setAttribute('aria-expanded', 'true');
    if (window.innerWidth < 750) { pop.style.left = ''; pop.style.top = ''; return; }
    const r = b.getBoundingClientRect();
    const w = pop.offsetWidth;
    pop.style.left = `${Math.max(16, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 16))}px`;
    pop.style.top = `${r.bottom + 10}px`;
  };

  const loadVideo = (wrap, startSeconds) => {
    const tpl = wrap.querySelector('template');
    let player = wrap.querySelector('iframe, video');
    if (!player && tpl) {
      wrap.appendChild(tpl.content.cloneNode(true));
      player = wrap.querySelector('iframe, video');
      const poster = wrap.querySelector('.gg-video__poster');
      if (poster) poster.remove();
      track('gg_video_play', { section: wrap.closest('.gg')?.id });
    }
    if (!player) return;
    if (startSeconds != null) {
      if (player.tagName === 'VIDEO') {
        const go = () => { player.currentTime = startSeconds; player.play(); };
        player.readyState > 0 ? go() : player.addEventListener('loadedmetadata', go, { once: true });
      } else {
        const u = new URL(player.src);
        u.searchParams.set('start', startSeconds);
        u.searchParams.set('autoplay', '1');
        u.hash = `t=${startSeconds}s`;
        player.src = u.toString();
      }
    } else if (player.tagName === 'VIDEO') {
      player.play().catch(() => {});
    }
    player.focus && player.focus();
  };

  /* ---------- Rail arrows ---------- */
  const initRail = (wrap) => {
    const rail = wrap.querySelector('.gg-rail');
    const prev = wrap.querySelector('[data-gg-prev]');
    const next = wrap.querySelector('[data-gg-next]');
    if (!rail || !prev || !next) return;
    const step = () => (rail.firstElementChild ? rail.firstElementChild.getBoundingClientRect().width + 28 : rail.clientWidth);
    const update = () => {
      prev.disabled = rail.scrollLeft < 4;
      next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
    };
    prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: reduceMotion() ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: reduceMotion() ? 'auto' : 'smooth' }));
    rail.addEventListener('scroll', update, { passive: true });
    update();
  };

  /* ---------- Stocking-stuffer pins ---------- */
  const closePops = (scope) => {
    scope.querySelectorAll('.gg-pop:not([hidden])').forEach((p) => (p.hidden = true));
    scope.querySelectorAll('.gg-pin[aria-expanded="true"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    scope.querySelectorAll('.gg-row.is-active').forEach((r) => r.classList.remove('is-active'));
  };
  const initPins = (section) => {
    section.querySelectorAll('.gg-pin').forEach((pin) => {
      pin.addEventListener('click', () => {
        const pop = document.getElementById(pin.getAttribute('aria-controls'));
        const open = pin.getAttribute('aria-expanded') === 'true';
        closePops(section);
        if (open || !pop) return;
        pop.hidden = false;
        pin.setAttribute('aria-expanded', 'true');
        const row = section.querySelector(`[data-gg-row="${pin.dataset.index}"]`);
        if (row) row.classList.add('is-active');
        (pop.querySelector('.gg-atc__btn') || pop).focus();
      });
    });
    section.querySelectorAll('.gg-pop__close').forEach((b) =>
      b.addEventListener('click', () => {
        const pop = b.closest('.gg-pop');
        closePops(section);
        const pin = section.querySelector(`[aria-controls="${pop.id}"]`);
        if (pin) pin.focus();
      })
    );
    section.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const pop = section.querySelector('.gg-pop:not([hidden])');
      if (!pop) return;
      closePops(section);
      const pin = section.querySelector(`[aria-controls="${pop.id}"]`);
      if (pin) pin.focus();
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.gg-pin, .gg-pop')) closePops(section);
    });
  };

  /* ---------- Pigment puff on Add to cart ---------- */
  // Red, yellow and blue from the Opus logo brushstrokes
  const PUFF_COLOURS = ['#c1273c', '#c1273c', '#f2c230', '#0b6399'];
  const puff = (btn) => {
    if (reduceMotion()) return;
    const r = btn.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    for (let i = 0; i < 22; i++) {
      const d = document.createElement('span');
      const s = 3 + Math.random() * 8;
      d.className = 'gg-puff';
      d.style.cssText = `left:${cx}px;top:${cy}px;width:${s}px;height:${s}px;background:${PUFF_COLOURS[Math.floor(Math.random() * PUFF_COLOURS.length)]};filter:blur(${(Math.random() * 1.6).toFixed(1)}px)`;
      document.body.appendChild(d);
      const a = Math.random() * Math.PI * 2;
      const dist = 20 + Math.random() * 56;
      const anim = d.animate(
        [
          { transform: 'translate(-50%,-50%) scale(1)', opacity: 0.95 },
          { transform: `translate(calc(-50% + ${Math.cos(a) * dist}px), calc(-50% + ${Math.sin(a) * dist - 22}px)) scale(.25)`, opacity: 0 },
        ],
        { duration: 650 + Math.random() * 450, easing: 'cubic-bezier(.2,.7,.3,1)' }
      );
      anim.onfinish = () => d.remove();
    }
  };
  let lastAtc = null;
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('.gg form[data-type="add-to-cart-form"]');
    if (form) {
      lastAtc = form.querySelector('.gg-atc__btn, .quick-add__submit');
      track('gg_add_to_cart', { section: form.closest('.gg')?.id, product: form.dataset.ggProduct });
    }
  }, true);
  const onCartUpdate = () => {
    if (!lastAtc) return;
    const btn = lastAtc;
    lastAtc = null;
    puff(btn);
    const label = btn.querySelector('[data-gg-label]');
    if (label) {
      const orig = label.textContent;
      label.textContent = label.dataset.added || 'Added ✓';
      setTimeout(() => (label.textContent = orig), 1400);
    }
  };

  /* ---------- Drop a hint on gift guide collection pages ----------
     The collection grid uses the theme's own card-product (not gg-card), so the button is added
     here instead of editing a core snippet. Dawn's facets replace #ProductGridContainer's contents
     on filter/sort/page, so a MutationObserver re-applies it. */
  const hintButton = (url, title) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gg-hint gg-hint--grid';
    b.setAttribute('data-gg-hint', '');
    b.dataset.url = url;
    b.dataset.title = title;
    b.setAttribute('aria-label', `Drop a hint: share ${title}`);
    b.innerHTML = '<svg aria-hidden="true" viewBox="0 0 20 20" width="15" height="15"><path d="M3 5.5h14v10H3z M3 6l7 5 7-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg><span>Drop a hint</span>';
    return b;
  };
  const addGridHints = (root) => {
    root.querySelectorAll('#product-grid > li').forEach((li) => {
      if (li.querySelector('[data-gg-hint]')) return;
      const link = li.querySelector('.card__heading a[href*="/products/"], a[href*="/products/"]');
      if (!link) return;
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.search = '';
      const title = (li.querySelector('.card__heading') || link).textContent.trim();
      li.classList.add('gg-hint-item');
      li.appendChild(hintButton(url.toString(), title));
    });
  };
  const initGridHints = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container || !document.querySelector('.gg')) return;
    addGridHints(container);
    new MutationObserver(() => addGridHints(container)).observe(container, { childList: true, subtree: true });
  };

  const init = () => {
    setStickyOffset();
    window.addEventListener('scroll', setStickyOffset, { passive: true });
    window.addEventListener('resize', setStickyOffset, { passive: true });
    document.querySelectorAll('[data-gg-nav]').forEach(initNav);
    document.querySelectorAll('[data-gg-filter]').forEach(initFilter);
    document.querySelectorAll('[data-gg-rail]').forEach(initRail);
    document.querySelectorAll('[data-gg-pins]').forEach(initPins);
    document.querySelectorAll('[data-gg-finder]').forEach(initFinder);
    document.querySelectorAll('[data-gg-tags]').forEach(initTags);
    document.querySelectorAll('[data-gg-bookshelf]').forEach(initBookshelf);
    document.querySelectorAll('[data-gg-xcards]').forEach(initXcards);
    initBack();
    placeCashback();
    // Lightbox: stop the video when it closes (Esc, ×, or backdrop click).
    document.querySelectorAll('[data-gg-lightbox]').forEach((d) => d.addEventListener('close', () => d.querySelector('[data-gg-lightbox-frame]').replaceChildren()));
    new MutationObserver((muts) => {
      if (muts.some((m) => [...m.addedNodes].some((nd) => nd.nodeType === 1 && (nd.matches('.gg-card-wrap, li') || nd.querySelector?.('.gg-card-wrap'))))) placeCashback();
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeHelps(); });
    window.addEventListener('scroll', () => { if (window.innerWidth >= 750) closeHelps(); }, { passive: true });
    initGridHints();
    initReveal();
    document.addEventListener('click', (e) => {
      const hint = e.target.closest('[data-gg-hint]');
      if (hint) { e.preventDefault(); dropHint(hint); return; }
      const poster = e.target.closest('.gg-video__poster');
      if (poster) return loadVideo(poster.closest('.gg-video'));
      const help = e.target.closest('[data-gg-help-toggle]');
      if (help) {
        const open = help.getAttribute('aria-expanded') !== 'true';
        closeHelps();
        if (open) openHelp(help);
        return;
      }
      if (!e.target.closest('.gg-help__pop')) closeHelps();
      const both = e.target.closest('[data-gg-add-both]');
      if (both) { e.preventDefault(); addBoth(both); return; }
      const boxVid = e.target.closest('[data-gg-box-video]');
      if (boxVid) {
        const sec = boxVid.closest('.gg');
        const dlg = sec && sec.querySelector('[data-gg-lightbox]');
        const tpl = sec && sec.querySelector(`[data-gg-box-video-tpl="${boxVid.dataset.ggBoxVideo}"]`);
        if (dlg && tpl && dlg.showModal) {
          const frame = dlg.querySelector('[data-gg-lightbox-frame]');
          frame.replaceChildren(tpl.content.cloneNode(true));
          dlg.showModal();
          track('gg_box_video', { id: boxVid.dataset.ggBoxVideo });
        }
        return;
      }
      if (e.target.closest('[data-gg-lightbox-close]') || e.target.matches('[data-gg-lightbox]')) {
        const dlg = e.target.closest('[data-gg-lightbox]');
        if (dlg) dlg.close();
        return;
      }
      const peek = e.target.closest('[data-gg-peek]');
      if (peek) {
        const on = peek.getAttribute('aria-pressed') !== 'true';
        peek.setAttribute('aria-pressed', String(on));
        peek.closest('.gg-box__media').classList.toggle('is-peek', on);
        return;
      }
      const watch = e.target.closest('[data-gg-watch]');
      if (watch) {
        // Staff pick tiles: jump to the Staff Picks video and play from that moment.
        const v = document.querySelector('#staff-picks .gg-video');
        if (v) {
          e.preventDefault();
          v.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
          loadVideo(v, Number(watch.dataset.ggWatch));
          track('gg_pick_watch', { at: watch.dataset.ggWatch });
        }
        return;
      }
      const ch = e.target.closest('[data-gg-chapter]');
      if (ch) {
        const v = document.getElementById(ch.dataset.target);
        if (v) {
          const r = v.getBoundingClientRect();
          if (r.top < 0 || r.bottom > window.innerHeight) v.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
          loadVideo(v, Number(ch.dataset.ggChapter));
        }
      }
    });
    // constants.js declares PUB_SUB_EVENTS with const, so it is global but not on window.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-gg-filter]').forEach(initFilter);
    document.querySelectorAll('[data-gg-rail]').forEach(initRail);
    document.querySelectorAll('[data-gg-pins]').forEach(initPins);
    document.querySelectorAll('[data-gg-nav]').forEach(initNav);
    document.querySelectorAll('[data-gg-finder]').forEach(initFinder);
    document.querySelectorAll('[data-gg-tags]').forEach(initTags);
    document.querySelectorAll('[data-gg-bookshelf]').forEach(initBookshelf);
    document.querySelectorAll('[data-gg-xcards]').forEach(initXcards);
    initBack();
  });
})();
